import React from 'react';
import { makeStyles } from 'tss-react/mui';

import Accordion, { AccordionProps } from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { units } from '../theme/units';

const useStyles = makeStyles<void, 'expanded'>()((theme, _params, classes) => ({
  root: {
    zIndex: 1,
    [`&.${classes.expanded}`]: {
      zIndex: 1,
      marginTop: 0,
      backgroundColor: theme.palette.grey[50],
    },
  },
  expanded: {},
  content: {
    zIndex: 1,
  },
  withMargin: {
    margin: units.trbl(1.25, 0, 1.25, 0),
  },
}));

export interface Props {
  summary: React.ReactNode;
  details: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  elevation?: AccordionProps['elevation'];
  square?: AccordionProps['square'];
  panelStyle?: React.CSSProperties;
  detailsClassname?: string;
  variant?: 'narrow' | 'default';
}

const Expandable: React.FC<Props> = ({
  summary,
  details,
  expanded,
  elevation,
  square,
  onChange,
  panelStyle,
  detailsClassname,
  variant = 'default',
  ...rest
}) => {
  const { classes, cx } = useStyles();
  const handleChange = onChange
    ? // eslint-disable-next-line @typescript-eslint/ban-types
      (_: React.ChangeEvent<{}>, isExpanded: boolean) => onChange(isExpanded)
    : undefined;

  const contentClass = cx(classes.content, {
    [classes.withMargin]: variant !== 'narrow',
  });

  return (
    <Accordion
      style={panelStyle}
      classes={{
        expanded: classes.expanded,
        root: classes.root,
      }}
      expanded={expanded}
      elevation={elevation}
      square={square}
      onChange={handleChange}
      {...rest}
    >
      <AccordionSummary
        classes={{
          root: classes.root,
          expanded: classes.expanded,
          content: contentClass,
        }}
        expandIcon={<ExpandMoreIcon data-test-locator="Icon - ExpandMore" />}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails className={detailsClassname}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export { Expandable };
